import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  useTheme,
  Paper
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getCompetitions } from 'src/apiCalls';
import { Competition } from 'src/interfaces';
import { useUserContext } from 'src/components/ProtectedRoute/userProvider';
import logger from 'src/logger';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HowToVoteIcon from '@mui/icons-material/HowToVote';

// Competition Card Component
const CompetitionCard = ({ competition }: { competition: Competition }) => {
  const theme = useTheme();
  const isActive = competition.status === 'active';
  const startDate = new Date(competition.startDate);
  const endDate = new Date(competition.endDate);
  const entryCutoffDate = competition.entryCutoffDate ? new Date(competition.entryCutoffDate) : endDate;
  
  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light}15 100%)`,
        borderRadius: '2px',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography 
            variant="h5" 
            component="h2" 
            gutterBottom
            sx={{
              fontWeight: 'bold',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {competition.title}
          </Typography>
          <Chip 
            label={isActive ? "Active" : "Ended"} 
            color={isActive ? "success" : "default"} 
            size="small"
            sx={{ 
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              borderRadius: '2px'
            }}
          />
        </Box>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          {competition.description}
        </Typography>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CalendarMonthIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Start Date:</strong> {startDate.toLocaleDateString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <HowToVoteIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              <strong>Entry Deadline:</strong> {entryCutoffDate.toLocaleDateString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <EmojiEventsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              <strong>End Date:</strong> {endDate.toLocaleDateString()}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            <strong>Prize:</strong> {competition.prize}
          </Typography>
        </Box>
      </CardContent>
      
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button 
          component={RouterLink} 
          to={`/competitions/view?id=${competition.id}`} 
          variant="contained" 
          color="primary" 
          fullWidth
          sx={{ 
            borderRadius: '2px',
            textTransform: 'none',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
            }
          }}
        >
          View Details
        </Button>
      </CardActions>
    </Card>
  );
};

// Main Component
const CompetitionsListPage = () => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const { user } = useUserContext();
  const theme = useTheme();

  useEffect(() => {
    const fetchCompetitions = async () => {
      try {
        setLoading(true);
        const data = await getCompetitions();
        setCompetitions(data || []);
      } catch (err) {
        logger.error('Error fetching competitions:', err);
        setError('Failed to load competitions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCompetitions();
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        textAlign: 'center',
        pt: 1,
      }}
    >
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: '2px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            mb: 3
          }}
        >
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{
              fontWeight: 'bold',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2
            }}
          >
            Fishing Photo Competitions
          </Typography>
          <Typography variant="h6" color="text.secondary" paragraph>
            Showcase your best catches and compete with fellow anglers in our monthly fishing photo contests
          </Typography>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        ) : competitions.length === 0 ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            No competitions are currently available. Check back later!
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {competitions.map((competition) => (
              <Grid item xs={12} sm={6} md={4} key={competition.id}>
                <CompetitionCard competition={competition} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default CompetitionsListPage; 